import { withTranslate } from "@pathwright/ui/src/components/"
import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import ErrorBoundary from "@pathwright/ui/src/components/error/ErrorBoundary"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import View from "@pathwright/ui/src/components/ui/View"
import useCohortPermissions from "@pathwright/web/src/modules/cohort/useCohortPermissions"
import Community from "@pathwright/web/src/modules/community/Community"
import { DISCUSSION_TYPE_QUESTION } from "@pathwright/web/src/modules/discussion/constants"
import {
  constructUrl,
  getCohortSectionBaseUrl,
  getCommunityAreaUrl,
  getGroupInviteUrl
} from "@pathwright/web/src/modules/utils/urls"
import { ConnectStores } from "lib/core/store"
import PropTypes from "prop-types"
import { useEffect } from "react"
import PathwrightAccountPermissions from "../../../../lib/core/components/pathwright/PathwrightAccountPermissions"
import { getDiscussionListHandlers } from "../discussion/utils"
import "./ResourceCommunity.css"

const ResourceCommunity = function (
  {
    params,
    resource,
    offering,
    learnerCount,
    toRegister,
    canRegisterForOffering,
    communityEnabled,
    t
  },
  context
) {
  const cohortPerms = useCohortPermissions({
    cohortId: offering && offering.id
  })
  const { communityArea } = params
  const { onSelectDiscussion, onStartDiscussion } = getDiscussionListHandlers({
    canStartDiscussion: cohortPerms.hasLearnerLevelAccess,
    // ensure offering is present
    baseRoute: offering
      ? constructUrl(
          getCommunityAreaUrl(resource.slug, offering.id, "discussion")
        )
      : null,
    // for community discussion, default discussion type to DISCUSSION_TYPE_QUESTION and limit types to [DISCUSSION_TYPE_QUESTION]
    type: DISCUSSION_TYPE_QUESTION,
    createTypes: [DISCUSSION_TYPE_QUESTION]
  })
  const tPrefix = "resource.community"
  const { can_access } = context.permissions

  useEffect(() => {
    if (offering && !communityEnabled) {
      // redirect user to the path if community is disabled -
      // likely better to handle this in the routes, but easier here where the
      // resource and offering are readily available
      App.navigate(getCohortSectionBaseUrl({ section: "path" }))
    }
  }, [communityEnabled])

  if (!offering) return null

  if (!can_access) {
    return (
      <BlankSlate
        heading={t(`${tPrefix}.sharing_questions`)}
        body={
          learnerCount
            ? `${learnerCount}+ ${t(`${tPrefix}.people_contributing`)}`
            : null
        }
        primaryAction={
          canRegisterForOffering
            ? {
                children: (
                  <>
                    {t(`${tPrefix}.register_to_unlock`)}{" "}
                    <Pathicon icon="chevron-right" />
                  </>
                ),
                to: toRegister
              }
            : null
        }
        inverted
      />
    )
  }

  const onChangeCommunityArea = (nextCommunityArea) => {
    if (communityArea !== nextCommunityArea) {
      // navigate to the selected community area
      App.navigate(
        getCommunityAreaUrl(resource.slug, offering.id, nextCommunityArea)
      )
    }
  }

  return (
    <ErrorBoundary>
      <PathwrightAccountPermissions ignoreActivated>
        {({ can_invite_to_offering, can_invite_to_resource }) => {
          const canInvite = offering.is_curriculum
            ? can_invite_to_resource
            : can_invite_to_offering

          return (
            <View className="ResourceCommunity">
              <Community
                resourceId={offering.resource_id}
                // source community loads all discussion based on resource ID alone,
                // thereby including discussions creaetd in child cohorts
                cohortId={offering.is_curriculum ? null : offering.id}
                onSelectDiscussion={onSelectDiscussion}
                // source community cannot start cohort discussion
                onStartDiscussion={
                  offering.is_curriculum ? null : onStartDiscussion
                }
                toInvite={
                  canInvite
                    ? getGroupInviteUrl(resource.slug, offering.id, "community")
                    : null
                }
                communityArea={communityArea}
                onChangeCommunityArea={onChangeCommunityArea}
              />
            </View>
          )
        }}
      </PathwrightAccountPermissions>
    </ErrorBoundary>
  )
}

ResourceCommunity.displayName = "ResourceCommunity"
ResourceCommunity.contextTypes = { permissions: PropTypes.object }

const mapStateToProps = function (state, ownProps) {
  const { resource, offering } = state.resourceStore.state

  const canRegisterForOffering =
    state.resourceStore.request.canRegisterForOffering()
  const toRegister = state.resourceStore.request.toRegisterLink()
  const communityEnabled = state.resourceStore.request.discussionEnabled()

  return _.assign(
    {},
    {
      resource,
      offering,
      toRegister,
      learnerCount: resource ? resource.total_registration_count : null,
      canRegisterForOffering,
      communityEnabled
    },
    ownProps
  )
}

const stores = [{ store: "resource", watch: ["resource", "offering"] }]

export default withTranslate(
  ConnectStores(ResourceCommunity, stores, {}, mapStateToProps)
)
